import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { Stack, Typography, Checkbox, IconButton, Chip } from '@mui/material';
import { Visibility as EyeIcon } from '@mui/icons-material';
import { loadAICompatibleNexsets, AICompatibleNexsetSource } from 'js/actions/aiTools';
import { GET_AI_COMPATIBLE_NEXSETS_REQUEST } from 'js/constants/actionTypes';
import { AvailableFilterTypesByView, SearchFilterViews } from 'js/constants/visibilityFilters';
import { MacroOptionsContextWrap } from 'js/contexts/MacroOptionsContext';
import SearchBar from 'js/components/MacroInput/SearchBar';
import { segmentsToString } from 'js/components/MacroInput/tools/segmentsToString';
import Constants from 'js/Constants';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { Nexset } from 'new/entities/Nexset';
import { Table } from 'new/elements/Table';
import { Column } from 'new/elements/Table/types';
import { SourceName } from 'new/elements/SourceName';
import { ChipBadge } from 'new/elements/ChipBadge';
import { Tabs } from 'new/elements/Tabs/Tabs';
import PipelineStatusBadge, {
  Status,
} from 'new/components/pipelines/PipelineStatusChip/PipelineStatusChip';
import { useSelector, useStore } from 'react-redux';
import { useInitialLoad } from 'new/hooks/useInitialLoad';
import { prepareNexset } from 'new/hooks/adapters/useMyNexsets';
import CardsListPageNumbers from 'js/components/elements/cards/CardsListPageNumbers';
import { useAccessControl } from 'new/state/app/repository/useAppRepository';
import { useFeatureFlag } from 'new/hooks/useFeatureFlags';
import { trim } from 'lodash';
import { NexsetItemSkeleton, SkeletonList } from '../../../components/ToolCard/ToolCardSkeleton';
import styles from './NexsetSelector.module.scss';

export interface SelectedNexset {
  id: string;
  name: string;
  description?: string;
  category?: string;
}

const SOURCE_TABS = [
  { value: 'owned', label: 'My Nexsets' },
  { value: 'marketplace', label: 'Data Products' },
];

export interface NexsetSelectorProps {
  selectedNexsets: SelectedNexset[];
  onSelectionChange: (nexsets: SelectedNexset[]) => void;
  onPreview?: (nexset: SelectedNexset) => void;
  multiSelect?: boolean;
  height?: number;
  hideSearch?: boolean;
  hideSelectionInfo?: boolean;
}

// Available filter types for this selector (same as MY_DATA_SETS)
const AVAILABLE_FILTER_TYPES = AvailableFilterTypesByView[SearchFilterViews.AI_COMPATIBLE_NEXSETS];

// Table columns with checkbox
const createColumns = (
  selectedIds: Set<string>,
  onToggle: (nexset: Nexset) => void,
  source: AICompatibleNexsetSource,
  onPreview?: (nexset: SelectedNexset) => void,
): Column<Nexset>[] => [
  {
    name: '',
    field: 'checkbox',
    width: 5,
    renderRow: (row) => (
      <Checkbox
        checked={selectedIds.has(row.id?.toString())}
        onChange={() => onToggle(row)}
        onClick={(e) => e.stopPropagation()}
        size="small"
      />
    ),
  },
  {
    name: 'Nexset',
    field: 'details',
    renderRow: (row) => {
      // `dataSource`, not `source`: the row's originating data source. Naming it
      // `source` shadowed the segment parameter above, so the badge test below
      // compared an object to a string and never fired.
      const { isDetected, source: dataSource, parentDataSet } = row as any;
      let description = parentDataSet?.name || '';
      let descriptionLabel = 'From nexset ';
      if (isDetected) {
        description = dataSource?.name || '';
        descriptionLabel = 'Detected from ';
      }
      return (
        <Stack direction="row" alignItems="center" gap="8px">
          <SourceName
            type={row.isDetected ? 'source' : 'nexset'}
            icon={(row as any).sourceIcon}
            name={row.name}
            description={descriptionLabel + description}
          />
          {source === 'marketplace' && (
            <ChipBadge label="Data Product" color="secondary" variant="outlined" />
          )}
        </Stack>
      );
    },
    width: 40,
  },
  {
    name: 'Status',
    field: 'status',
    width: 15,
    renderRow: (row) => (
      <PipelineStatusBadge
        id={row.id}
        revertDirection
        status={row.status as Status}
        runtimeStatus={row?.runtime_status}
        ingestionMode={row.ingestion_mode}
        parentArchivalStatus={row?.archival_status}
      />
    ),
  },
  {
    name: 'ID',
    field: 'id',
    width: 10,
    renderRow: (row) => <ChipBadge color="success" label={row.id + ''} />,
    center: true,
  },
  {
    name: '',
    field: 'preview',
    width: 5,
    renderRow: (row) =>
      onPreview && row.id ? (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onPreview({
              id: String(row.id),
              name: row.name,
              description: row.description,
            });
          }}
        >
          <EyeIcon fontSize="small" />
        </IconButton>
      ) : null,
  },
];

export const NexsetSelector: React.FC<NexsetSelectorProps> = ({
  selectedNexsets,
  onSelectionChange,
  onPreview,
  multiSelect = true,
  height = 400,
  hideSearch = false,
  hideSelectionInfo = false,
}) => {
  const dispatch = useAppDispatch();
  const prevFiltersRef = useRef<string>('');
  const requestIdRef = useRef(0);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const { mode: aclQueryMode } = useAccessControl();

  // Data Products are behind a flag: the segment is the only way to reach them,
  // so hiding it also stops the marketplace query from ever being issued. Tools
  // already built from one keep working — this gates discovery, not use.
  const marketplaceToolsEnabled = useFeatureFlag('UI_AI_MARKETPLACE_DATA_PRODUCT_TOOLS');

  // Which set is on screen. The two sets overlap but paginate independently, so
  // each keeps its own page rather than sharing one.
  const [selectedSource, setSource] = useState<AICompatibleNexsetSource>('owned');
  // Derived, not trusted: if the flag turns off while the segment is selected,
  // the tabs disappear but the state would still ask for Data Products.
  const source: AICompatibleNexsetSource = marketplaceToolsEnabled ? selectedSource : 'owned';
  const [pageBySource, setPageBySource] = useState<Record<AICompatibleNexsetSource, number>>({
    owned: 1,
    marketplace: 1,
  });
  const currentPage = pageBySource[source];
  const setCurrentPage = useCallback(
    (page: number) => setPageBySource((prev) => ({ ...prev, [source]: page })),
    [source],
  );

  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [nexsetIds, setNexsetIds] = useState<number[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Get loading state from Redux request state
  const reduxLoading = useSelector(
    (state: any) => state.request?.[GET_AI_COMPATIBLE_NEXSETS_REQUEST] ?? false,
  );
  const isLoading = useInitialLoad({
    reduxLoadingSelector: () => reduxLoading,
    hasDataSelector: () => nexsetIds.length > 0,
  });

  // Get filters from Redux for AI_COMPATIBLE_NEXSETS view
  const filters = useSelector(
    (state: any) =>
      state.app?.visibilityFilters?.filters?.[SearchFilterViews.AI_COMPATIBLE_NEXSETS],
  );

  // Get store for preparing nexsets with metadata (avoids subscribing to entire state)
  const store = useStore();
  // Only subscribe to the entities slice that contains nexset data
  const dataSetEntities = useSelector((state: any) => state.entities?.dataSets);

  // Convert filters to string for comparison
  const filtersAsString = useMemo(
    () => trim(segmentsToString(Array.isArray(filters) ? filters : [])),
    [filters],
  );

  // Reset to page 1 when filters or access mode change.
  useEffect(() => {
    prevFiltersRef.current = filtersAsString;
    setPageBySource({ owned: 1, marketplace: 1 });
  }, [filtersAsString, aclQueryMode]);

  // Load marketplace-eligible nexsets using the dedicated action.
  // Uses a request counter to ignore stale responses from superseded requests
  // (e.g. when aclQueryMode changes and triggers a page reset + refetch).
  useEffect(() => {
    const thisRequestId = ++requestIdRef.current;
    setLoadError(null);
    dispatch(
      loadAICompatibleNexsets({
        page: currentPage,
        sortCriteria: Constants.SortCriteria.LAST_CREATED_DESC,
        aclQueryMode,
        source,
      }),
    )
      .then((response: any) => {
        if (thisRequestId !== requestIdRef.current) return;

        const pages = response?.totalPages ?? 1;
        const newIds: number[] = response?.result || [];
        const items = response?.totalItems ?? newIds.length;

        setTotalPages(pages);
        setTotalItems(items);
        setNexsetIds(newIds);
      })
      .catch(() => {
        if (thisRequestId !== requestIdRef.current) return;
        setLoadError('Failed to load Nexsets. Please try again.');
        setNexsetIds([]);
      });
  }, [dispatch, currentPage, filtersAsString, aclQueryMode, source]);

  // Handle page change (using onListingChange pattern to avoid setCurrentPage prop requirement)
  const handleListingChange = useCallback(
    ({ type, value }: { type: string; value: number }) => {
      if (type === 'updateCurrentPage') {
        setCurrentPage(value);
      }
    },
    // setCurrentPage is itself keyed on `source`, so an empty dep array would
    // pin this to the segment that was active on first render and page the
    // wrong one after a tab switch.
    [setCurrentPage],
  );

  // Prepare nexsets from IDs using the Redux state. Active-only filtering is
  // done server-side by loadAICompatibleNexsets, so we only drop ids whose
  // entity hasn't landed in the store yet.
  const accumulatedNexsets = useMemo(() => {
    if (!dataSetEntities) return [];
    const currentState = store.getState();
    return nexsetIds
      .map((id) => {
        const nexset = dataSetEntities[id];
        if (!nexset) return null;
        return prepareNexset(nexset, currentState);
      })
      .filter((nexset): nexset is Nexset => nexset !== null);
  }, [nexsetIds, dataSetEntities, store]);

  const selectedIds = useMemo(() => new Set(selectedNexsets.map((n) => n.id)), [selectedNexsets]);

  const handleToggle = useCallback(
    (nexset: Nexset) => {
      const nexsetId = nexset.id?.toString();
      const exists = selectedIds.has(nexsetId);

      if (exists) {
        onSelectionChange(selectedNexsets.filter((n) => n.id !== nexsetId));
      } else {
        const newNexset: SelectedNexset = {
          id: nexsetId,
          name: nexset.name,
          description: nexset.description,
        };

        if (multiSelect) {
          onSelectionChange([...selectedNexsets, newNexset]);
        } else {
          onSelectionChange([newNexset]);
        }
      }
    },
    [selectedIds, selectedNexsets, onSelectionChange, multiSelect],
  );

  const handleRowClick = useCallback(
    (row: Nexset) => {
      handleToggle(row);
    },
    [handleToggle],
  );

  const columns = useMemo(
    () => createColumns(selectedIds, handleToggle, source, onPreview),
    [selectedIds, handleToggle, source, onPreview],
  );

  const isInitialLoading = isLoading && currentPage === 1 && nexsetIds.length === 0;
  const hasActiveFilters = filtersAsString.length > 0;

  return (
    <Stack className={styles.container}>
      {/* Source switch. Deliberately labelled "Data Products" and nothing
          stronger: v1 cannot verify that a custodian approved this user, so the
          UI must not claim it. See loadAICompatibleNexsets. */}
      {marketplaceToolsEnabled && (
        <Tabs
          value={source}
          onChange={(_, value) => setSource(value as AICompatibleNexsetSource)}
          tabs={SOURCE_TABS}
        />
      )}

      {/* Search Bar with access level toggle and pagination */}
      {!hideSearch && (
        <div className={styles.controlsRow}>
          <div className={styles.searchBarContainer}>
            <MacroOptionsContextWrap>
              <SearchBar
                view={SearchFilterViews.AI_COMPATIBLE_NEXSETS}
                inModal={true}
                placeholder="Search by name, description, ID, tags..."
                availableFilterTypes={AVAILABLE_FILTER_TYPES}
                isClientSideSearch={false}
              />
            </MacroOptionsContextWrap>
          </div>
          <CardsListPageNumbers
            isLoading={isLoading}
            listing={{ currentPage, totalPages, totalItems }}
            onListingChange={handleListingChange}
          />
        </div>
      )}

      {/* Selection info */}
      {!hideSelectionInfo && selectedNexsets.length > 0 && (
        <div className={styles.selectionInfo}>
          <Typography variant="caption" color="text.secondary">
            {selectedNexsets.length} Nexset
            {selectedNexsets.length !== 1 ? 's' : ''} selected
          </Typography>
          <div className={styles.selectedChips}>
            {selectedNexsets.slice(0, 3).map((nexset) => (
              <Chip
                key={nexset.id}
                label={nexset.name}
                size="small"
                variant="outlined"
                onDelete={() =>
                  onSelectionChange(selectedNexsets.filter((n) => n.id !== nexset.id))
                }
              />
            ))}
            {selectedNexsets.length > 3 && (
              <Chip label={`+${selectedNexsets.length - 3} more`} size="small" variant="outlined" />
            )}
          </div>
        </div>
      )}

      {/* Table */}
      <div
        ref={tableContainerRef}
        className={styles.tableContainer}
        style={{ height, overflowY: 'auto' }}
      >
        {isInitialLoading ? (
          <SkeletonList count={6} component={NexsetItemSkeleton} spacing={0} />
        ) : loadError ? (
          <Stack alignItems="center" justifyContent="center" height="100%">
            <Typography variant="body2" color="error">
              {loadError}
            </Typography>
          </Stack>
        ) : accumulatedNexsets.length === 0 ? (
          <Stack alignItems="center" justifyContent="center" height="100%">
            <Typography variant="body2" color="text.secondary">
              {hasActiveFilters
                ? 'No Nexsets match your search criteria.'
                : source === 'marketplace'
                  ? 'No Data Products available. Request access to one from the Marketplace to use it as a tool.'
                  : 'No Nexsets available.'}
            </Typography>
          </Stack>
        ) : (
          <Table
            loading={isLoading}
            parentScrollRef={tableContainerRef}
            columns={columns}
            rows={accumulatedNexsets}
            onRowClick={handleRowClick}
            getRowClassName={(row) =>
              selectedIds.has(row.id?.toString()) ? styles.selectedRow : ''
            }
          />
        )}
      </div>
    </Stack>
  );
};
